import React, { useRef, useState } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Divider,
  FormControlLabel, Radio, RadioGroup,
} from '@mui/material';
import {
  DatosProducto as DatosProductoType,
  DatosPaquete, PAQUETE_INITIAL, PAQUETE_CO_INITIAL,
  TipoProducto, PesoDocumento,
} from './EnvioNacionalModal.types';
import { PaqueteItem, getPaqueteFieldError } from './PaqueteItem';
import { formatMilesCO, parseMilesCO } from './EnvioNacionalModal.utils';

const compactFieldSx = {
  '& .MuiFormHelperText-root': { mt: 0.75, mx: 0, minHeight: '1.1em', lineHeight: 1.2 },
};

const NUMERIC_RE = /\D/g;
const ALPHANUMERIC_RE = /[^a-zA-Z0-9\s.,#_áéíóúÁÉÍÓÚñÑüÜ-]/g;

const VALOR_DECLARADO_MAX_DOCUMENTO = 900;

export const getValorDeclaradoError = (val: string, tipo?: TipoProducto, esColombia = false): string => {
  if (esColombia && tipo === 'paquete') return '';
  if (!val) return 'Campo requerido';
  if (val.length > 10) return 'Máximo 10 dígitos';
  if (tipo === 'documento' && !esColombia && Number(val) > VALOR_DECLARADO_MAX_DOCUMENTO) {
    return `Para documentos el valor declarado no puede superar $${VALOR_DECLARADO_MAX_DOCUMENTO}`;
  }
  return '';
};

export const isDatosProductoValido = (
  datos: DatosProductoType,
  esColombia = false,
  modoCotizador = false,
): boolean => {
  if (!esColombia) {
    if (getValorDeclaradoError(datos.valorDeclarado, datos.tipo) !== '') return false;
  }
  if (datos.tipo === 'paquete') {
    const campos: (keyof DatosPaquete)[] = modoCotizador
      ? ['largo', 'alto', 'ancho', 'peso']
      : ['largo', 'alto', 'ancho', 'peso', 'contenido'];
    return datos.paquetes.every(p =>
      campos.every(f => getPaqueteFieldError(f, p, esColombia) === '')
      && (!esColombia || getPaqueteFieldError('valorDeclarado', p, true) === '')
    );
  }
  if (!modoCotizador && !datos.contenidoDocumento.trim()) return false;
  if (esColombia) {
    const vd = getValorDeclaradoError(datos.valorDeclarado, 'documento', true);
    if (vd !== '') return false;
  }
  return true;
};

export const calcValorDeclaradoGuia = (paquetes: DatosPaquete[]): number =>
  paquetes.reduce((acc, p) => {
    const vd = Number(p.valorDeclarado ?? 0) || 0;
    return acc + vd * p.cantidad;
  }, 0);

type PaqueteTouched = Partial<Record<keyof DatosPaquete, boolean>>[];

interface Props {
  datos: DatosProductoType;
  onChange: (field: keyof DatosProductoType, value: unknown) => void;
  esColombia?: boolean;
  modoCotizador?: boolean;
}

export const DatosProducto: React.FC<Props> = ({
  datos, onChange, esColombia = false, modoCotizador = false,
}) => {
  const [valorDeclaradoTouched, setValorDeclaradoTouched] = useState(false);
  const [contenidoDocTouched, setContenidoDocTouched] = useState(false);
  const [paquetesTouched, setPaquetesTouched] = useState<PaqueteTouched>([{}]);
  const pkgCounter = useRef(1);
  const [paqueteKeys, setPaqueteKeys] = useState<string[]>(['pkg-0']);

  const handleTipo = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    onChange('tipo', value as TipoProducto);
  };

  const handlePesoDocumento = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    onChange('pesoDocumento', value as PesoDocumento);
  };

  const handleValorDeclarado = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = esColombia ? parseMilesCO(e.target.value) : e.target.value.replaceAll(NUMERIC_RE, '');
    onChange('valorDeclarado', digits.slice(0, 10));
  };

  const handleContenidoDocumento = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('contenidoDocumento', e.target.value.replaceAll(ALPHANUMERIC_RE, ''));
  };

  const handleReferencia = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('referenciaDocumento', e.target.value.replaceAll(ALPHANUMERIC_RE, '').slice(0, 30));
  };

  const handlePaqueteChange = (index: number, field: keyof DatosPaquete, value: string | number) => {
    const updated = datos.paquetes.map((p, i) => i === index ? { ...p, [field]: value } : p);
    onChange('paquetes', updated);
  };

  const handlePaqueteBlur = (index: number, field: keyof DatosPaquete) => {
    setPaquetesTouched(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: true };
      return next;
    });
  };

  const handleAgregarPaquete = () => {
    const nuevo = esColombia ? { ...PAQUETE_CO_INITIAL } : { ...PAQUETE_INITIAL };
    onChange('paquetes', [...datos.paquetes, nuevo]);
    setPaquetesTouched(prev => [...prev, {}]);
    setPaqueteKeys(prev => [...prev, `pkg-${pkgCounter.current++}`]);
  };

  const handleEliminarPaquete = (index: number) => {
    onChange('paquetes', datos.paquetes.filter((_, i) => i !== index));
    setPaquetesTouched(prev => prev.filter((_, i) => i !== index));
    setPaqueteKeys(prev => prev.filter((_, i) => i !== index));
  };

  const radioCardSx = (selected: boolean) => ({
    border: '1px solid',
    borderColor: selected ? 'primary.main' : 'divider',
    borderRadius: 1,
    px: 1.5,
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    flex: 1,
  });

  const radioLabelSx = {
    m: 0,
    '& .MuiFormControlLabel-label': { fontSize: 14 },
    '& .MuiRadio-root': { p: 0.5 },
  };

  const valorDeclaradoGuia = esColombia && datos.tipo === 'paquete'
    ? calcValorDeclaradoGuia(datos.paquetes)
    : null;

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary"
        sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Tipo de producto
      </Typography>

      {/* Radio cards Paquetes / Documentos */}
      <RadioGroup row value={datos.tipo} onChange={handleTipo} sx={{ gap: 1, mb: 2 }}>
        <Box sx={radioCardSx(datos.tipo === 'paquete')}>
          <FormControlLabel value="paquete" control={<Radio size="small" />} label="Paquetes" sx={radioLabelSx} />
        </Box>
        <Box sx={radioCardSx(datos.tipo === 'documento')}>
          <FormControlLabel value="documento" control={<Radio size="small" />} label="Documentos" sx={radioLabelSx} />
        </Box>
      </RadioGroup>

      {/* ── Modo Paquetes ── */}
      {datos.tipo === 'paquete' && (
        <Box>
          {datos.paquetes.map((paquete, idx) => (
            <React.Fragment key={paqueteKeys[idx] ?? idx}>
              {idx > 0 && <Divider sx={{ my: 2 }} />}
              <PaqueteItem
                index={idx}
                datos={paquete}
                onChange={handlePaqueteChange}
                touched={paquetesTouched[idx] ?? {}}
                onBlur={handlePaqueteBlur}
                onEliminar={handleEliminarPaquete}
                canEliminar={datos.paquetes.length > 1}
                esColombia={esColombia}
                modoCotizador={modoCotizador}
              />
            </React.Fragment>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="text" color="primary"
              onClick={handleAgregarPaquete}
              sx={{ fontWeight: 600, textTransform: 'none' }}
            >
              Agregar paquete +
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Modo Documentos ── */}
      {datos.tipo === 'documento' && (
        <Box>
          <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
            Selecciona el peso aproximado de tus documentos.
          </Typography>

          <RadioGroup row value={datos.pesoDocumento} onChange={handlePesoDocumento} sx={{ gap: 1, mb: 1.5 }}>
            <Box sx={radioCardSx(datos.pesoDocumento === '1')}>
              <FormControlLabel value="1" control={<Radio size="small" />} label="1 Kg" sx={radioLabelSx} />
            </Box>
            <Box sx={radioCardSx(datos.pesoDocumento === '2')}>
              <FormControlLabel value="2" control={<Radio size="small" />} label="2 Kg" sx={radioLabelSx} />
            </Box>
          </RadioGroup>

          <Grid container spacing={1}>
            {esColombia && !modoCotizador && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth size="small" sx={compactFieldSx} label="Referencia"
                  value={datos.referenciaDocumento ?? ''}
                  onChange={handleReferencia}
                  helperText={`${(datos.referenciaDocumento ?? '').length}/30`}
                  slotProps={{ htmlInput: { maxLength: 30 } }}
                />
              </Grid>
            )}

            {esColombia && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth required size="small" sx={compactFieldSx}
                  label="Valor declarado de la guía"
                  value={formatMilesCO(datos.valorDeclarado)}
                  onChange={handleValorDeclarado}
                  onBlur={() => setValorDeclaradoTouched(true)}
                  error={valorDeclaradoTouched && !!getValorDeclaradoError(datos.valorDeclarado, 'documento', true)}
                  helperText={valorDeclaradoTouched ? getValorDeclaradoError(datos.valorDeclarado, 'documento', true) : undefined}
                  inputMode="numeric"
                  slotProps={{ htmlInput: { maxLength: 13 } }}
                />
              </Grid>
            )}

            {!modoCotizador && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth required size="small" sx={compactFieldSx}
                  label="Contenido"
                  value={datos.contenidoDocumento}
                  onChange={handleContenidoDocumento}
                  onBlur={() => setContenidoDocTouched(true)}
                  error={contenidoDocTouched && !datos.contenidoDocumento.trim()}
                  helperText={contenidoDocTouched && !datos.contenidoDocumento.trim() ? 'Campo requerido' : undefined}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Valor declarado de la guía global — no aplica en cotizador Colombia */}
      <Grid container spacing={1} sx={{ mt: 0.5 }}>
        <Grid size={{ xs: 12 }}>
          {esColombia && datos.tipo === 'paquete' && !modoCotizador ? (
            <TextField
              fullWidth disabled size="small" sx={compactFieldSx}
              label="Valor declarado de la guía"
              value={valorDeclaradoGuia?.toLocaleString('es-CO') ?? '0'}
              helperText="Suma automática del valor declarado de los paquetes"
            />
          ) : (
            !esColombia && (
              <TextField
                fullWidth required size="small" sx={compactFieldSx}
                label="Valor declarado de la guía"
                value={datos.valorDeclarado}
                onChange={handleValorDeclarado}
                onBlur={() => setValorDeclaradoTouched(true)}
                error={valorDeclaradoTouched && !!getValorDeclaradoError(datos.valorDeclarado, datos.tipo)}
                helperText={valorDeclaradoTouched ? getValorDeclaradoError(datos.valorDeclarado, datos.tipo) : undefined}
                inputMode="numeric"
                slotProps={{ htmlInput: { maxLength: 10 } }}
              />
            )
          )}
        </Grid>
      </Grid>

    </Box>
  );
};
